import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket, CONNECTION_STATUS } from '../../context/WebSocketContext';
import ThemeDropdown from '../ThemeDropdown';
import AlertBell from '../AlertBell/AlertBell';
import NotificationList from '../NotificationList/NotificationList';
import AlertMenu from '../AlertMenu/AlertMenu';
import { Link } from 'react-router-dom';
import NotificationService from '../../services/NotificationService';


function Header({ headerMenuDisabled = false }) {
  const { userData, logout } = useAuth();
  const { connectionStatus, error, connect } = useWebSocket();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(Notification?.permission || 'default');

  /**
   * Checks browser support and updates notification permission state
   */
  useEffect(() => {
    const checkNotificationPermission = async () => {
      try {
        // Check if notifications are supported in this browser
        if (!('Notification' in window)) {
          console.log('Desktop notifications are not supported in this browser');
          return;
        }
        
        // Update state with current permission
        setNotificationPermission(Notification.permission);
      } catch (error) {
        console.error('Error checking notification permission:', error);
      }
    };
    
    checkNotificationPermission();
  }, []);


  
  // Connection status handling
  const getConnectionStatusClass = () => {
    switch (connectionStatus) {
      case CONNECTION_STATUS.CONNECTED:
        return 'status-connected';
      case CONNECTION_STATUS.CONNECTING:
        return 'status-connecting';
      case CONNECTION_STATUS.ERROR:
        return 'status-error';
      case CONNECTION_STATUS.DISCONNECTED:
      default:
        return 'status-disconnected';
    }
  };
  
  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case CONNECTION_STATUS.CONNECTED:
        return 'Connected';
      case CONNECTION_STATUS.CONNECTING:
        return 'Connecting';
      case CONNECTION_STATUS.ERROR:
        return 'Connection Error';
      case CONNECTION_STATUS.DISCONNECTED:
      default:
        return 'Disconnected';
    }
  };
  
  const handleConnectClick = () => {
    if (connectionStatus === CONNECTION_STATUS.DISCONNECTED || 
        connectionStatus === CONNECTION_STATUS.ERROR) {
      connect();
    }
  };
  
  /**
   * Toggles notification permission
   */
  const toggleNotificationPermission = async () => {
    try {
      if (!('Notification' in window)) {
        window.alert('Desktop notifications are not supported in your browser');
        return;
      }
      
      // Get browser-specific instructions
      const getBrowserInstructions = () => {
        const isChromeOrEdge = navigator.userAgent.includes('Chrome') || navigator.userAgent.includes('Edg');
        const isFirefox = navigator.userAgent.includes('Firefox');
        const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
        
        if (isChromeOrEdge) {
          return 'In Chrome/Edge: Click the lock icon in the address bar → Site settings → Notifications';
        } else if (isFirefox) {
          return 'In Firefox: Click the lock icon in the address bar → Connection secure → More information → Permissions → Notifications';
        } else if (isSafari) {
          return 'In Safari: Safari menu → Preferences → Websites → Notifications → Find this website';
        } else {
          return 'Check your browser settings to manage notification permissions.';
        }
      };
      
      if (notificationPermission === 'granted') {
        // Show disable instructions
        const instructions = getBrowserInstructions();
        window.alert(`To disable notifications: \n\n${instructions}\n\nAfter changing settings, you may need to reload the page.`);
      } 
      else if (notificationPermission === 'denied') {
        // Show instructions for enabling in browser settings
        const instructions = getBrowserInstructions();
        window.alert(`Notifications are blocked. To enable them: \n\n${instructions}\n\nAfter changing settings, you may need to reload the page.`);
      }
      else {
        // Default state - request permission
        const notificationService = new NotificationService();
        const permission = await notificationService.requestNotificationPermission();
        setNotificationPermission(permission);
        console.log(`Updated notification permission: ${permission}`);
      }
    } catch (error) {
      console.error('Error managing notification permission:', error);
    }
  };

  return (
    <header className="app-header">
      <div className="logo-menu-container">
        <div className="logo-container">
          <h1>DeepR Dashboard</h1>
        </div>
        <nav className="header-nav">
          <Link to="/" className="alert-menu-button home-link">
            <span className="alert-menu-text">Home</span>
          </Link>
        </nav>
        <AlertMenu 
          disabled={headerMenuDisabled}
        />
      </div>
      
      <div className="status-container">
        <div className="connection-status-indicator">
          <div 
            className={`status-indicator ${getConnectionStatusClass()}`}
            onClick={handleConnectClick}
            title={error || getConnectionStatusText()}
          >
            <div className="status-dot"></div>
            <span className="status-text">{getConnectionStatusText()}</span>
          </div>
        </div>
      </div>
      
      <div className="user-container">
        {userData && (
          <div className="user-info">
            <span className="user-name">{userData.user_name || 'User'}</span>
            <div className="notification-container">
              <AlertBell onClick={() => setShowNotifications(!showNotifications)} />
              <NotificationList 
                isOpen={showNotifications} 
                onClose={() => setShowNotifications(false)} 
              />
            </div>
            {/* Notification Permission Toggle Button */}
            <button 
              className={`notification-status-button ${notificationPermission}`}
              onClick={toggleNotificationPermission}
              title={notificationPermission === 'granted' ? 'Manage notifications' : 'Enable notifications'}
            >
              <span className="notification-label">Notifications</span>
              {notificationPermission === 'granted' ? (
                <svg className="notification-status-icon check" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
                </svg>
              ) : notificationPermission === 'denied' ? (
                <svg className="notification-status-icon cross" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor" />
                </svg>
              ) : (
                <svg className="notification-status-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor" />
                </svg>
              )}
            </button>
            <ThemeDropdown />
            <button className="logout-button" onClick={logout}>Logout</button>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;